import { UserActionTypes, UserSearchParams } from './types';
import { AUTH_TYPE } from 'types';
import { all, takeLatest, call, put, select, delay } from 'redux-saga/effects';
import Services from 'services';
import {
  resetAppAuthState,
  setAppUserRole,
  setIsSignOutCompleted
} from 'redux/app/actions';
import {
  fetchUserFailure,
  fetchUserSuccess,
  fetchUserSearchSuccess,
  fetchUserSearchFailure,
  fetchApiKeysFailure,
  fetchApiKeysStart,
  fetchApiKeysSuccess,
  addApiKeysFailure,
  deleteApiKeysFailure,
  updateUserFailure,
  updateUserSuccess,
  updateUserDone,
  contactSupportSuccess,
  contactSupportFailure,
  // Temporary block - need to delete after Helix enabled
  fetchRemedyForceUserStart,
  fetchRemedyForceUserSuccess,
  fetchRemedyForceUserFailure,
  // Temporary block
  getAddressSearchSuggestSuccess,
  getAddressSearchSuggestFailure
} from './actions';
import { setAuthenticated, setUserSession } from 'redux/session/actions';
import Utils from 'utilities/helper.util';
import { buildUserPatchPayload } from 'utilities/userPatch.util';
import { fetchAuthFailure } from 'redux/auth/action';

// Temporary block - need to delete after Helix enabled
let checkedRemedyForceUser = false;
// Temporary block

export function* handleFetchUser() {
  const getAppState = (state: any) => state.app;
  const appState = yield select(getAppState);
  try {
    const timezone = Utils.getTimeZone();
    const auth = yield call(Services.auth.fetchCurrentUser, appState.loginType);
    if (auth?.user) {
      if (appState.loginType === AUTH_TYPE.EXTERNAL_APP_LOGIN) {
        const dummyUser = {
          id: 0,
          username: 'xxxxxx',
          email: 'xxxxxx',
          role: 'SYSADMIN',
          isVerified: true,
          firstName: 'External',
          lastName: 'App'
        };
        if (appState.isSignOutCompleted) {
          yield put(setIsSignOutCompleted(false));
        }
        yield put(setAppUserRole('SYSADMIN'));
        yield put(setUserSession(dummyUser));
        yield put(fetchUserSuccess(dummyUser));

        return;
      }
      const user = yield call(Services.user.fetchUser);

      if (!user?.error) {
        if (!user.preferences?.timezone) {
          const preferences = { ...user?.preferences, timezone };
          yield call(Services.user.patchUserDetails, { preferences });
        }
        if (appState.isSignOutCompleted) {
          yield put(setIsSignOutCompleted(false));
        }
        yield put(setAppUserRole(user?.role));
        yield put(setUserSession(user));
        yield put(fetchUserSuccess(user));
        // Temporary block - need to delete after Helix enabled
        if (
          !checkedRemedyForceUser &&
          process.env.REACT_APP_REMEDY_FORCE_ENABLED === 'true'
        ) {
          yield put(fetchRemedyForceUserStart());
          checkedRemedyForceUser = true;
        }
        // Temporary block
      } else {
        yield put(fetchAuthFailure(user.error));
        yield put(fetchUserFailure(user.error));
      }
    }
  } catch (error) {
    yield put(setUserSession({}));
    yield put(setAuthenticated(false));
    yield put(fetchUserFailure(error));
    yield put(resetAppAuthState());
  }
}

export function* onFetchUser() {
  yield takeLatest(UserActionTypes.FETCH_USER_START, handleFetchUser);
}

// SEARCH USER
export function* handleUserSearch(req: any) {
  const params: UserSearchParams = req.payload;
  try {
    const res = yield call(Services.user.fetchUserBySearchTerm, params);
    if (!res.error) {
      yield put(fetchUserSearchSuccess(res.Users));
    } else {
      yield put(fetchUserSearchFailure(res.error.message));
    }
  } catch (error) {
    const errorMsg = error.message ? error.message : error;
    yield put(fetchUserSearchFailure(errorMsg));
  }
}

export function* doFetchApiKeys() {
  try {
    const response = yield call(Services.user.fetchApiKeysById);
    if (response.error) {
      throw new Error(response.error.message);
    }
    yield put(fetchApiKeysSuccess(response));
  } catch (error) {
    yield put(fetchApiKeysFailure(error.message));
  }
}

export function* doGenerateApiKeys() {
  try {
    const response = yield call(Services.user.generateApiKeyForUser);
    if (response.error) {
      throw new Error(response.error.message);
    }
    yield put(fetchApiKeysStart());
  } catch (error) {
    yield put(addApiKeysFailure(error.message));
  }
}

export function* doUpdateUserProfile(req: any) {
  try {
    const getUserCache = (state: any) => state.userCache;
    const userCache = yield select(getUserCache);
    const patchPayload = buildUserPatchPayload(userCache?.user, req.payload);

    if (Object.keys(patchPayload).length === 0) {
      yield put(updateUserSuccess(userCache?.user));
      yield delay(1000);
      yield put(updateUserDone());
      return;
    }

    const response = yield call(Services.user.patchUserDetails, patchPayload);
    if (response.error) {
      throw new Error(response.error.message);
    }
    try {
      yield put(updateUserSuccess(response));
      yield delay(1000);
      yield put(updateUserDone());
      yield delay(3000);
      const userResponse = yield call(Services.user.fetchUser);
      if (userResponse.error) {
        throw new Error(userResponse.error.message);
      }
      yield put(fetchUserSuccess(userResponse));
    } catch (error) {
      yield put(fetchUserFailure(error.message));
    }
  } catch (error) {
    yield put(updateUserFailure(error.message));
  }
}
export function* doDeleteApiKeys(request: any) {
  try {
    yield call(Services.user.deleteApiKeyForUser, request.payload.apiKeyId);
    yield put(fetchApiKeysStart());
  } catch (error) {
    //TODO put alerts
    //TODO change name to generate
    yield put(deleteApiKeysFailure(error.message));
  }
}

export function* doContactSupportStart(params: any) {
  const { role, request } = params.payload;
  try {
    let response;
    if (role) {
      response = yield call(Services.user.authenticatedSupportService, request);
    } else {
      response = yield call(
        Services.user.unAuthenticatedSupportService,
        request
      );
    }

    if (!response?.error) {
      yield put(contactSupportSuccess('submitted'));
    } else {
      yield put(contactSupportFailure('Error occoured on Request submission'));
    }
  } catch (error) {
    yield put(contactSupportFailure(error.message));
  }
}

function* doGetAddressSearchSuggest(request: any) {
  try {
    const response = yield call(
      Services.map.getPlaceSuggest,
      request.payload.datasets,
      request.payload.query,
      request.payload.fields,
      request.payload.limit
    );
    if (response?.Results) {
      yield put(getAddressSearchSuggestSuccess(response.Results));
    } else {
      yield put(
        getAddressSearchSuggestFailure('Unable to get place suggestion.')
      );
    }
  } catch (err) {
    const msg = err?.message || err.toString();
    put(getAddressSearchSuggestFailure(msg));
  }
}

// Temporary block - need to delete after Helix enabled
function* doFetchRemedyForceUserStart() {
  try {
    const response = yield call(Services.user.checkRemedyForceUser);
    if (response) {
      yield put(fetchRemedyForceUserSuccess(response.clientFound));
    } else {
      yield put(fetchRemedyForceUserSuccess(false));
    }
  } catch (e) {
    yield put(fetchRemedyForceUserFailure(e));
  }
}
// Temporary block

function* onGenerateApiKeys() {
  yield takeLatest(UserActionTypes.ADD_APIKEYS_START, doGenerateApiKeys);
}

function* onFetchApiKeys() {
  yield takeLatest(UserActionTypes.FETCH_APIKEYS_START, doFetchApiKeys);
}

function* onDeleteApiKeys() {
  yield takeLatest(UserActionTypes.DELETE_APIKEYS_START, doDeleteApiKeys);
}

export function* onUserSearch() {
  yield takeLatest(UserActionTypes.FETCH_USER_SEARCH_START, handleUserSearch);
}

export function* doUpdateProfileFlag() {}
function* onUpdateProfileFlag() {
  yield takeLatest(UserActionTypes.PROFILE_UPDATING_FLAG, doUpdateProfileFlag);
}
function* onUpdateUserProfile() {
  yield takeLatest(UserActionTypes.UPDATE_USER_START, doUpdateUserProfile);
}
function* onContactSupportStart() {
  yield takeLatest(
    UserActionTypes.CONTACT_SUPPORT_START,
    doContactSupportStart
  );
}
function* onGetAddressSearchSuggest() {
  yield takeLatest(
    UserActionTypes.GET_ADDRESS_SEARCH_SUGGESTION_START,
    doGetAddressSearchSuggest
  );
}

// Temporary block - need to delete after Helix enabled
function* onFetchRemedyForceUserStart() {
  yield takeLatest(
    UserActionTypes.FETCH_REMEDY_FORCE_USER_START,
    doFetchRemedyForceUserStart
  );
}
// Temporary block

export function* userSagas() {
  yield all([
    call(onFetchUser),
    call(onUserSearch),
    call(onFetchApiKeys),
    call(onGenerateApiKeys),
    call(onDeleteApiKeys),
    call(onUpdateProfileFlag),
    call(onUpdateUserProfile),
    call(onContactSupportStart),
    // Temporary block - need to delete after Helix enabled
    call(onFetchRemedyForceUserStart),
    // Temporary block
    call(onGetAddressSearchSuggest)
  ]);
}

export default userSagas;
